import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import MainFooter from "../../components/footer/MainFooter";
import {
  handleNewPasswordValidationAndSetErrors,
  handlePasswordValidationAndSetErrors,
} from "../../utils/Errors/Login/passwordValidationError";
import {
  commonErrorToastMessage,
  commonSuccessToastMessage,
  emptyChangePassword,
  incorrectOldPassword,
  passwordChangedSuccess,
} from "../../components/toastify/AllToastifyErrors";
import { changePassword } from "../../api/changePassword";

const ChangePassword = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const [isChangePassword, setIsChangePassword] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [oldPasswordMismatch, setOldPasswordMismatch] = useState(false);

  // Function to handle password input change
  const handleOldPasswordChange = (e) => {
    const { value } = e.target;
    setUserData({ ...userData, password: value });

    // Validate Password
    handlePasswordValidationAndSetErrors(value, setErrors);
  };

  // Function to handle new password input change
  const handleNewPasswordChange = (e) => {
    const { value } = e.target;
    setUserData({ ...userData, newPassword: value });

    // Validate Password
    handleNewPasswordValidationAndSetErrors(value, setErrors);

    // Check if the new password and confirm password match
    // setPasswordsMatch(value === userData.confirmPassword);
  };

  // change password API calling
  const handleChangePassword = async () => {
    setIsChangePassword(true);

    if (
      !userData.password ||
      !userData.newPassword ||
      !userData.confirmPassword
    ) {
      commonErrorToastMessage("Please enter your password.");
      setIsChangePassword(false);
      return;
    }

    if (userData.password === userData.newPassword) {
      commonErrorToastMessage(
        "New password cannot be the same as the old password."
      );
      setIsChangePassword(false);
      return;
    }

    if (userData.newPassword !== userData.confirmPassword) {
      commonErrorToastMessage(
        "New Password and Confirm Password does not match."
      );
      setIsChangePassword(false);
      return;
    }

    try {
      const id = localStorage.getItem("Id");

      const dataToChangePassword = [
        id,
        userData.password,
        userData.newPassword,
        userData.confirmPassword,
      ];

      const response = await changePassword(...dataToChangePassword);

      if (errors.newPassword) {
        commonErrorToastMessage("Please enter a strong password.");
        return;
      }


      if (response.status === 200) {
        // Password changed successfully
        commonSuccessToastMessage(
          "Your password has been updated successfully."
        );
        setIsChangePassword(true);
        navigate("/");
      } else {
        console.error("API Error:", response.data.error);
        setErrorMessage("Failed to change password. Please try again.");
      }
    }catch (error) {
      if (error?.response?.status === 400) {
        commonErrorToastMessage("Oops! Your previous password is incorrect.");
        console.error("Invalid Password");
        setOldPasswordMismatch(true);
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        commonErrorToastMessage("Your session has expired. Please log in again.");
      } else {
        setErrorMessage("Network error occurred. Please try again.");
      }
    }    
  };

  const handleCancelChangePassword = () => {
    navigate("/userinfo");
  };

  const dummyPasswordField = (
    <input
      type="password"
      autoComplete="new-password"
      style={{ display: "none" }}
      readOnly
    />
  );

  return (
    <div className="">
      <AppNavigation />
      <div className="flex flex-col items-center justify-center h-[81vh] change-password">
        <div className=" py-5 text-center">
          <h1 className="text-[#212529] text-3xl font-medium">
            Change Password
          </h1>
        </div>

        <div className="w-1/3 mx-auto">
          <div className="mb-3 flex font-medium">
            <p className="">Email :</p>
            <p className="px-2 ">{localStorage.getItem("email")}</p>
          </div>

          {/* Old Password Input */}
          {dummyPasswordField}
          <p className="mb-3">
            <input
              className="w-full border border-[#ced4da] p-2  "
              placeholder="Old Password"
              type="password"
              value={userData.password}
              onChange={handleOldPasswordChange}
            />
            {errors.password && (
              <span className="text-red-500 antialiased animate-fade-slide-down text-[13px] signup-error-message text-start">
                {errors.password}
              </span>
            )}
          </p>

          {/* New Password Input */}
          <p className="mb-3">
            <input
              type="password"
              className="w-full border border-[#ced4da] p-2 rounded"
              placeholder="New Password "
              name="newPassword" // Add this attribute
              value={userData?.newPassword}
              onChange={handleNewPasswordChange}
            />
            {errors.newPassword && (
              <span className="text-red-500 antialiased animate-fade-slide-down text-[13px] signup-error-message text-start">
                {errors.newPassword}
              </span>
            )}
          </p>

          {/* Confirm Password Input */}
          <p className="mb-3">
            <input
              type="password"
              autoComplete="false"
              className="w-full border border-[#ced4da] p-2 rounded"
              placeholder="Confirm Password"
              name="confirmPassword"
              value={userData.confirmPassword}
              onChange={(e) => {
                const { value } = e.target;
                setUserData({ ...userData, confirmPassword: value });
                setPasswordsMatch(userData.newPassword === value);
              }}
            />
            {!passwordsMatch && (
              <span className="text-red-500 antialiased animate-fade-slide-down text-[13px] signup-error-message">
                New Password and Confirm Password does not match.
              </span>
            )}
          </p>
        </div>

        {/* Change Password Button */}
        <div className="flex gap-4 place-content-center align-middle">
          <Tooltip
            title="Change your password"
            arrow
            placement="top"
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
          >
            <button
              onClick={handleChangePassword}
              className={`rounded mt-2 py-2 hover:opacity-90 bg-[#f4833d] border-[#f4833d] text-white   `}
            >
              <span className=" font-semibold mx-8">Change Password</span>
            </button>
          </Tooltip>

          {/* Cancel Button */}
          <Tooltip
            title="Cancel"
            arrow
            placement="top"
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 300 }}
          >
            <button
              onClick={handleCancelChangePassword}
              className={`rounded mt-2 py-2 hover:opacity-90 text-[#f4833d] hover:bg-[#6c757d] hover:border-[#6c757d] hover:text-white border border-[#f4833d]  `}
            >
              <span className=" font-semibold mx-8">Cancel</span>
            </button>
          </Tooltip>
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default ChangePassword;
